import { computeDestinationPoint, getCenterOfBounds, getDistance, getRhumbLineBearing } from 'geolib';

import {
  computeAngle, flipPoint, getScale, rotate,
} from '../components/track/ground/utils';
import { GenericValueType } from '../query/session';
import { GroundType } from '../models/ground';

export interface GPSCoords {
  latitude: number;
  longitude: number;
}

export type PathPoint = {x: number | null, y: number | null, t: number, s: number | null, h: number | null, hr?: number | null};
export type PathData = Array<PathPoint>;

export const geolibAccuracy = 0.01;

export const gpsToLocal = (
  gps1: GPSCoords,
  gps2: GPSCoords,
  angle = 0,
  abs = false,
  scale = 1,
) => {
  if (!gps1.latitude || !gps1.longitude || !gps2.latitude || !gps2.longitude) {
    return {
      x: 0,
      y: 0,
    }
  }

  const dx = (gps1.latitude >= gps2.latitude ? -1 : 1) * getDistance(
    { latitude: gps1.latitude, longitude: gps1.longitude },
    { latitude: gps2.latitude, longitude: gps1.longitude },
    geolibAccuracy,
  );

  const dy = (gps1.longitude >= gps2.longitude ? -1 : 1) * getDistance(
    { latitude: gps1.latitude, longitude: gps1.longitude },
    { latitude: gps1.latitude, longitude: gps2.longitude },
    geolibAccuracy,
  );

  if (angle !== 0) {
    // Converti l'angolo da gradi a radianti
    const theta = angle * Math.PI / 180;

    const x = dx * Math.cos(theta) - dy * Math.sin(theta);
    const y = dx * Math.sin(theta) + dy * Math.cos(theta);

    return {
      x: (abs ? Math.abs(x) : x) * scale,
      y: (abs ? Math.abs(y) : y) * scale,
    };
  }

  return {
    x: (abs ? Math.abs(dx) : dx) * scale,
    y: (abs ? Math.abs(dy) : dy) * scale,
  };
};

export const gpsToLocalFast = (
  gps1: GPSCoords,
  gps2: GPSCoords,
  angle = 0,
  abs = false,
  scale = 1,
) => {
  // Validazione minimale inline
  if (!(gps1.latitude && gps1.longitude && gps2.latitude && gps2.longitude)) {
    return { x: 0, y: 0 };
  }

  const lat1 = gps1.latitude;
  const lon1 = gps1.longitude;
  const lat2 = gps2.latitude;
  const lon2 = gps2.longitude;

  // Calcolo ultra-veloce per piccole distanze (approssimazione equirettangolare)
  const dLat = lat2 - lat1;
  const dLon = lon2 - lon1;
  const lat1Rad = lat1 * 0.017453292519943295; // Pre-calcolato Math.PI / 180

  let x = dLon * Math.cos(lat1Rad) * 111320; // metri per grado di longitudine
  let y = dLat * 111320; // metri per grado di latitudine

  // Rotazione ottimizzata
  if (angle !== 0) {
    const theta = angle * 0.017453292519943295;
    const cosTheta = Math.cos(theta);
    const sinTheta = Math.sin(theta);
    const tempX = x;
    x = tempX * cosTheta - y * sinTheta;
    y = tempX * sinTheta + y * cosTheta;
  }

  // Operazioni finali combinate
  if (scale !== 1) {
    x *= scale;
    y *= scale;
  }

  if (abs) {
    x = Math.abs(x);
    y = Math.abs(y);
  }

  return { x, y };
};

export const getBearing = (gps1: GPSCoords, gps2: GPSCoords) => gps1.latitude && gps1.longitude && gps2.latitude && gps2.longitude && getRhumbLineBearing(
  { latitude: gps1.latitude, longitude: gps1.longitude },
  { latitude: gps2.latitude, longitude: gps2.longitude },
);

export const LPSFromGPSPoint = (
  point: PathPoint,
  positionA: GPSCoords,
  positionB: GPSCoords,
  positionC: GPSCoords,
  scale = 1,
) => {

  if (!positionA.latitude || !positionB.latitude) {
    return [];
  }

  const bearing = getBearing(
    {
      latitude: positionA.latitude,
      longitude: positionA.longitude,
    },
    {
      latitude: positionB.latitude,
      longitude: positionB.longitude,
    },
  );

  console.log('LPSFromGPSPoint', bearing);

  const offsetY = getDistance(
    {
      latitude: positionA.latitude,
      longitude: positionA.longitude,
    },
    {
      latitude: positionB.latitude,
      longitude: positionB.longitude,
    },
    geolibAccuracy,
  );

  console.log('LPSFromGPSPoint', offsetY);

  const offsetX = getDistance(
    {
      latitude: positionB.latitude,
      longitude: positionB.longitude,
    },
    {
      latitude: positionC.latitude,
      longitude: positionC.longitude,
    },
    geolibAccuracy,
  );

  console.log('LPSFromGPSPoint', offsetX);

  if (!point.x || !point.y) {
    return {
      t: point.t,
      x: null,
      y: null,
      s: point.s,
      h: point.h,
      hr: point.hr,
      x_0: null,
      y_0: null,
    };
  }

  console.log('LPSFromGPSPoint - gpsToLocalExtreme', point.x, point.y, positionC.latitude, positionC.longitude, bearing);

  const { x, y } = gpsToLocalExtreme(
    {
      latitude: point.x,
      longitude: point.y,
    },
    {
      latitude: positionC.latitude,
      longitude: positionC.longitude,
    },
    90 - bearing,
  );

  console.log('LPSFromGPSPoint - x,y', x, y);

  return {
    t: point.t,
    x: x * scale + offsetX * scale,
    y: y * scale - offsetY * scale,
    x_0: point.x,
    y_0: point.y,
    s: point.s,
    h: point.h,
    hr: point.hr,
  };
};

export const LPSFromGPSPath = (
  path: PathData,
  positionA: GPSCoords,
  positionB: GPSCoords,
  positionC: GPSCoords,
  scale = 1,
) => {

  if (!positionA.latitude || !positionB.latitude) {
    return [];
  }

  const bearing = getBearing(
    {
      latitude: positionA.latitude,
      longitude: positionA.longitude,
    },
    {
      latitude: positionB.latitude,
      longitude: positionB.longitude,
    },
  );

  const offsetY = getDistance(
    {
      latitude: positionA.latitude,
      longitude: positionA.longitude,
    },
    {
      latitude: positionB.latitude,
      longitude: positionB.longitude,
    },
    geolibAccuracy,
  );
  const offsetX = getDistance(
    {
      latitude: positionB.latitude,
      longitude: positionB.longitude,
    },
    {
      latitude: positionC.latitude,
      longitude: positionC.longitude,
    },
    geolibAccuracy,
  );

  return path.map((p) => {
    if (!p.x || !p.y) {
      return {
        t: p.t,
        x: null,
        y: null,
        s: p.s,
        h: p.h,
        hr: p.hr,
      };
    }

    const { x, y } = gpsToLocalExtreme(
      {
        latitude: p.x,
        longitude: p.y,
      },
      {
        latitude: positionC.latitude,
        longitude: positionC.longitude,
      },
      90 - bearing,
    );

    return {
      t: p.t,
      x: x * scale + offsetX * scale,
      y: y * scale - offsetY * scale,
      x_0: p.x,
      y_0: p.y,
      s: p.s,
      h: p.h,
      hr: p.hr,
    };
  });
};

type VertexType = { x: number; y: number };

interface GPSGroundReturnType {
  vertexA: VertexType;
  vertexC: VertexType;
  width: number;
  height: number;
  scale: number;
  scaledWidth: number;
  scaledHeight: number;
}

export const GPSGround = (props: Partial<GroundType>, boxWidth: number, boxHeight: number): GPSGroundReturnType => {
  const {
    vertexAX,
    vertexAY,
    vertexCX,
    vertexCY,
  } = props;

  let vertexA = {
    x: typeof vertexAX?.value === 'number' ? vertexAX?.value : parseFloat(vertexAX?.value),
    y: typeof vertexAY?.value === 'number' ? vertexAY?.value : parseFloat(vertexAY?.value),
  };
  let vertexC = {
    x: typeof vertexCX?.value === 'number' ? vertexCX?.value : parseFloat(vertexCX?.value),
    y: typeof vertexCY?.value === 'number' ? vertexCY?.value : parseFloat(vertexCY?.value),
  };

  if (vertexA?.x !== undefined && vertexC?.x !== undefined && vertexA.x > vertexC.x) {
    const swap = vertexC;
    vertexC = vertexA;
    vertexA = swap;
  }

  if (vertexA?.y !== undefined && vertexC?.y !== undefined && vertexA?.y < vertexC?.y) {
    const swap = vertexC.y;
    vertexC.y = vertexA.y;
    vertexA.y = swap;
  }

  const width = vertexC.x !== undefined && vertexA.x !== undefined
    ? vertexC.x - vertexA.x
    : 0;

  const height = vertexC.y !== undefined && vertexA.y !== undefined
    ? vertexA.y - vertexC.y
    : 0;
  const scale = getScale(width, height, boxWidth, boxHeight) * 0.85;

  vertexA = {
    x:
      rotate(
        flipPoint({ x: vertexAX?.value, y: vertexAY?.value }, props),
        computeAngle(props),
      ).x * scale,
    y:
      rotate(
        flipPoint({ x: vertexAX?.value, y: vertexAY?.value }, props),
        computeAngle(props),
      ).y * scale,
  };
  vertexC = {
    x:
      rotate(
        flipPoint({ x: vertexCX?.value, y: vertexCY?.value }, props),
        computeAngle(props),
      ).x * scale,
    y:
      rotate(
        flipPoint({ x: vertexCX?.value, y: vertexCY?.value }, props),
        computeAngle(props),
      ).y * scale,
  };

  const scaledWidth = vertexC.x !== undefined && vertexA.x !== undefined ? vertexC.x - vertexA.x : 1;
  const scaledHeight = vertexA.y - vertexC.y ? vertexA.y - vertexC.y : 1;

  return {
    vertexA,
    vertexC,
    width,
    height,
    scale,
    scaledWidth,
    scaledHeight,
  };
};

export interface LPSToGPSGroundProps {
  vertexAX: GenericValueType;
  vertexAY: GenericValueType;
  vertexCX: GenericValueType;
  vertexCY: GenericValueType;
}

export const LPSToGPSGround = (props: LPSToGPSGroundProps) => {
  const {
    vertexAX,
    vertexAY,
    vertexCX,
    vertexCY,
  } = props;

  let vertexA = {
    x: typeof vertexAX?.value === 'number' ? vertexAX?.value : parseFloat(vertexAX?.value),
    y: typeof vertexAY?.value === 'number' ? vertexAY?.value : parseFloat(vertexAY?.value),
  };
  let vertexC = {
    x: typeof vertexCX?.value === 'number' ? vertexCX?.value : parseFloat(vertexCX?.value),
    y: typeof vertexCY?.value === 'number' ? vertexCY?.value : parseFloat(vertexCY?.value),
  };

  if (vertexA?.x !== undefined && vertexC?.x !== undefined && vertexA.x > vertexC.x) {
    const swap = vertexC;
    vertexC = vertexA;
    vertexA = swap;
  }

  if (vertexA?.y !== undefined && vertexC?.y !== undefined && vertexA?.y < vertexC?.y) {
    const swap = vertexC.y;
    vertexC.y = vertexA.y;
    vertexA.y = swap;
  }

  const width = vertexC.x !== undefined && vertexA.x !== undefined
    ? vertexC.x - vertexA.x
    : 0;

  const height = vertexC.y !== undefined && vertexA.y !== undefined
    ? vertexA.y - vertexC.y
    : 0;
  const scale = getScale(width + 10, height + 10);

  vertexA = {
    x:
      rotate(
        flipPoint({ x: vertexAX?.value, y: vertexAY?.value }, props),
        computeAngle(props),
      ).x * scale,
    y:
      rotate(
        flipPoint({ x: vertexAX?.value, y: vertexAY?.value }, props),
        computeAngle(props),
      ).y * scale,
  };
  vertexC = {
    x:
      rotate(
        flipPoint({ x: vertexCX?.value, y: vertexCY?.value }, props),
        computeAngle(props),
      ).x * scale,
    y:
      rotate(
        flipPoint({ x: vertexCX?.value, y: vertexCY?.value }, props),
        computeAngle(props),
      ).y * scale,
  };

  const scaledWidth = vertexC.x !== undefined && vertexA.x !== undefined ? vertexC.x - vertexA.x : 1;
  const scaledHeight = vertexA.y - vertexC.y ? vertexA.y - vertexC.y : 1;

  return {
    vertexA,
    vertexC,
    width,
    height,
    scale,
    scaledWidth,
    scaledHeight,
  };
};

export const deg2rad = (degrees: number) => {
  return degrees * (Math.PI / 180);
}

export const rad2deg = (radians: number) => {
  return radians * (180 / Math.PI);
}

export const calculateNewPoint = (start: GPSCoords, bearing: number, distance: number): GPSCoords => {
  const R = 6371; // Radius of the Earth in kilometers
  const angDist = distance / R; // Angular distance in radians
  const baringAng = deg2rad(bearing); // Bearing in radians

  const radLatitude = deg2rad(start.latitude);
  const radLongitude = deg2rad(start.longitude);

  const newRadLatitude = Math.asin(Math.sin(radLatitude) * Math.cos(angDist) + Math.cos(radLatitude) * Math.sin(angDist) * Math.cos(baringAng));
  const newRadLongitude = radLongitude + Math.atan2(Math.sin(baringAng) * Math.sin(angDist) * Math.cos(radLatitude), Math.cos(angDist) - Math.sin(radLatitude) * Math.sin(newRadLatitude));

  const latitude = rad2deg(newRadLatitude);
  const longitude = rad2deg(newRadLongitude);

  return { latitude, longitude };
}

export const generateInnerRectangle = (
  percent: number,
  coordinates: [GPSCoords, GPSCoords, GPSCoords, GPSCoords],
): [GPSCoords, GPSCoords, GPSCoords, GPSCoords] => {
  /*if (percent <= 0 || percent >= 100) {
    throw new Error('Percent must be between 0 and 100 (exclusive)');
  }*/

  const center = getCenterOfBounds(coordinates);

  const ratio = Math.sqrt(percent / 100);

  return coordinates.map((coord) => {
    const bearing = getRhumbLineBearing(center, coord);
    const initialDistance = getDistance(center, coord);
    const reducedDistance = initialDistance * ratio;

    return computeDestinationPoint(center, reducedDistance, bearing);
  }) as [GPSCoords, GPSCoords, GPSCoords, GPSCoords];
}


// prove ottimizzazione
// Versione con lookup table per cos/sin (per angoli fissi comuni)
const ANGLE_CACHE = new Map();
const precomputeAngle = (angle) => {
  if (!ANGLE_CACHE.has(angle)) {
    const theta = angle * 0.017453292519943295;
    ANGLE_CACHE.set(angle, {
      cos: Math.cos(theta),
      sin: Math.sin(theta)
    });
  }
  return ANGLE_CACHE.get(angle);
};

export const gpsToLocalUltra = (
  gps1: GPSCoords,
  gps2: GPSCoords,
  angle = 0,
  abs = false,
  scale = 1,
) => {
  if (!(gps1.latitude | 0) && gps1.latitude !== 0) return { x: 0, y: 0 };
  if (!(gps1.longitude | 0) && gps1.longitude !== 0) return { x: 0, y: 0 };
  if (!(gps2.latitude | 0) && gps2.latitude !== 0) return { x: 0, y: 0 };
  if (!(gps2.longitude | 0) && gps2.longitude !== 0) return { x: 0, y: 0 };

  const dLat = gps2.latitude - gps1.latitude;
  const dLon = gps2.longitude - gps1.longitude;

  // approssimazione Taylor
  const lat1Rad = gps1.latitude * 0.017453292519943295;
  const cosLat = 1 - (lat1Rad * lat1Rad * 0.5); // Approssimazione cos(x) ≈ 1 - x²/2

  let x = dLon * cosLat * 111320;
  let y = dLat * 111320;

  // Rotazione con lookup cache
  if (angle !== 0) {
    const { cos: cosTheta, sin: sinTheta } = precomputeAngle(angle);
    const tempX = x;
    x = tempX * cosTheta - y * sinTheta;
    y = tempX * sinTheta + y * cosTheta;
  }

  if (scale !== 1) {
    if (abs) {
      return {
        x: Math.abs(x * scale),
        y: Math.abs(y * scale),
      };
    }
    return { x: x * scale, y: y * scale };
  }

  if (abs) {
    return {
      x: Math.abs(x),
      y: Math.abs(y),
    };
  }

  return { x, y };
};

export const gpsToLocalExtreme = (() => {
  const DEG_TO_RAD = 0.017453292519943295;
  const METERS_PER_DEGREE = 111320;

  // Pre-allocato per evitare garbage collection
  const result = { x: 0, y: 0 };

  return (gps1, gps2, angle = 0, abs = false, scale = 1) => {
    const { latitude: lat1, longitude: lon1 } = gps1;
    const { latitude: lat2, longitude: lon2 } = gps2;

    if (!lat1 || !lon1 || !lat2 || !lon2) {
      result.x = 0;
      result.y = 0;
      return result;
    }

    const dLat = lat2 - lat1;
    const dLon = lon2 - lon1;

    // Approssimazione coseno ultra-veloce
    const lat1Rad = lat1 * DEG_TO_RAD;
    const lat1RadSq = lat1Rad * lat1Rad;
    const cosLat = 1 - lat1RadSq * 0.5 + lat1RadSq * lat1RadSq * 0.041666666666666664; // Termini Taylor x²/2 - x⁴/24

    let x = dLon * cosLat * METERS_PER_DEGREE;
    let y = dLat * METERS_PER_DEGREE;

    // Rotazione inline
    if (angle !== 0) {
      const theta = angle * DEG_TO_RAD;
      // Fast sin/cos per angoli piccoli
      const sinTheta = theta - (theta * theta * theta) * 0.16666666666666666; // x - x³/6
      const cosTheta = 1 - (theta * theta) * 0.5; // 1 - x²/2

      const tempX = x;
      x = tempX * cosTheta - y * sinTheta;
      y = tempX * sinTheta + y * cosTheta;
    }

    if (scale !== 1) {
      x *= scale;
      y *= scale;
    }

    result.x = abs ? Math.abs(x) : x;
    result.y = abs ? Math.abs(y) : y;

    return result;
  };
})();
